/**
 * Exports, SEZ supplies and deemed exports (FR-E9, FR-E10; e2e-filing B-01..B-03), in the
 * words the client profile and the Returns screen show.
 *
 * The service decides everything — which rows are exports, the export type each books as,
 * the LUT a month reads — and serves the month's standing as `exports` on every tables read.
 * These functions only say it.
 */

import type { ExportsState, Lut, SezBuyer } from './api'

/** `2026-04-01` → `1 Apr 2026`. A date the screen cannot read is shown as it came. */
export function shownDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return iso
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${Number(m[3])} ${months[Number(m[2]) - 1]} ${m[1]}`
}

/** One LUT as a line: `AD27… — for 2026-27` (D-143: a LUT is one financial year's). */
export function lutWords(lut: Pick<Lut, 'number' | 'financial_year'>): string {
  return `${lut.number} — for ${lut.financial_year}`
}

/**
 * `2026-27` as the service reads it (CLAUDE.md §5), or null when the year cannot be read:
 * four digits, a dash, and the next year's last two (or all four) digits.
 */
export function financialYearOf(text: string): string | null {
  const m = /^\s*(\d{4})\s*-\s*(\d{2}|\d{4})\s*$/.exec(text)
  if (!m) return null
  const start = Number(m[1])
  const end = Number(m[2])
  if (end !== (start + 1) % 100 && end !== start + 1) return null
  return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
}

/** How an SEZ buyer was confirmed (D-16): never "auto". */
export function sezSourceWords(buyer: Pick<SezBuyer, 'derived_from' | 'register_says'>): string {
  if (buyer.derived_from === 'register') {
    return buyer.register_says
      ? `The GST register says “${buyer.register_says}”, confirmed by a person`
      : 'The GST register says SEZ, confirmed by a person'
  }
  return 'Confirmed on a person’s own word'
}

/** Whether the register's taxpayer type names an SEZ (`SEZ Unit`, `SEZ Developer`). */
export function registerSaysSez(registrationType: string | null | undefined): boolean {
  return (registrationType ?? '').toLowerCase().includes('sez')
}

/** Why a LUT the person typed cannot be saved yet, or null when it can. */
export function lutProblem(draft: { number: string; financial_year: string }): string | null {
  if (!draft.number.trim()) return 'Type the LUT’s number (the ARN or the letter’s reference).'
  if (!financialYearOf(draft.financial_year)) return 'Type the financial year it is for, like 2026-27.'
  return null
}

/** Why a buyer GSTIN cannot be confirmed yet, or null. The service checks the checksum. */
export function sezGstinProblem(gstin: string, ownGstin?: string | null): string | null {
  const g = gstin.trim().toUpperCase()
  if (g.length !== 15) return 'A GSTIN is 15 characters.'
  if (!/^[0-9]{2}[A-Z0-9]{13}$/.test(g)) return 'A GSTIN starts with a two-digit state code and has only letters and digits.'
  // N31: said as it is typed, and gone as soon as the box changes. Only the service's refusal
  // said it — after Confirm, and then it outlived the input it was about. The service keeps
  // its own check (`returns/exports.py`); this one is for the person, in the same words.
  if (ownGstin && g === ownGstin.trim().toUpperCase()) return "That is this client's own GSTIN, not a buyer's."
  return null
}

/** The card's parts an error can be about (N31): its two forms, and reading its details. */
export type ExportFactsSection = 'lut' | 'sez' | 'load'

export type SectionErrors = Partial<Record<ExportFactsSection, string>>

/**
 * The errors once a part's input changes: that part's error goes, the others' stay. The card
 * kept one error for everything, cleared only when the next action started, so a refusal
 * about a GSTIN stayed above the next GSTIN typed (N31, UI-S4-18).
 */
export function withoutError(errors: SectionErrors, section: ExportFactsSection): SectionErrors {
  if (!(section in errors)) return errors
  const { [section]: _gone, ...rest } = errors
  return rest
}

/** The sentence under the Returns tabs when the month has exports, SEZ supplies or deemed exports. */
export function exportsStateWords(exports: ExportsState | undefined): string | null {
  if (!exports || exports.state === 'none') return null
  if (exports.state === 'held') {
    return 'This month’s exports and SEZ supplies are all held for review, so Table 6 carries nothing yet — see the gates panel.'
  }
  const parts: string[] = []
  const n = (count: number | undefined, one: string, many: string) =>
    count ? `${count} ${count === 1 ? one : many}` : null
  const counted = [
    n(exports.export_invoices, 'export invoice (Table 6A)', 'export invoices (Table 6A)'),
    n(exports.sez_invoices, 'invoice to an SEZ (Table 6B)', 'invoices to SEZ units (Table 6B)'),
    n(exports.deemed_invoices, 'deemed export (Table 6C)', 'deemed exports (Table 6C)'),
  ].filter((p): p is string => p !== null)
  if (counted.length) parts.push(`This month has ${counted.join(', ')}.`)
  const years = Object.entries(exports.luts ?? {}).sort(([a], [b]) => a.localeCompare(b))
  parts.push(
    years.length
      ? `A LUT is recorded for ${years.map(([y, n]) => `${y} (${n})`).join(', ')}, so supplies made under it carry no tax; one made without tax in a year with no LUT is held.`
      : 'No LUT is recorded, so these are with payment of IGST; one made without tax is held until a LUT is recorded.',
  )
  const pending = exports.shipping_bill_pending ?? []
  if (pending.length) {
    parts.push(
      `${pending.length === 1 ? 'Export' : 'Exports'} ${pending.join(', ')} ${pending.length === 1 ? 'has' : 'have'} no shipping bill yet — add it on Review when customs issues it.`,
    )
  }
  return parts.join(' ')
}
